import { inject, injectable } from 'inversify';
import { CHANNEL_EVENTS, CLIENT_EVENTS } from '../../constants/events';
import EnhancedEventEmitter from '../../EnhancedEventEmitter';
import { logResponse } from '../../helpers/peer';
import { TOKEN } from '../../inversify.tokens';
import Logger from '../Logger';
import { Peers } from '../Peers';
import { serializeError } from 'serialize-error';
import ChannelStateSyncEventHandler from './ChannelStateSyncEventHandler';
import type { ChannelEvent } from '../../proto/generated/channel_message/channel_event';
import { ChannelEventType } from '../../proto/generated/channel_message/channel_event';
import ChannelStateSynchronizer from '../ChannelStateSynchronizer/ChannelStateSynchronizer';
import {
  convertChannelMessagePeerToPeerResponse,
  convertChannelMessagePeerGroupsToPeerGroups,
  convertPeerGroupsToChannelMessagePeerGroups,
  convertChannelMessageProducerLabelsToTrackLabels,
} from '../../helpers/channelMessageConverter';
import { ActivityConfirmationRequiredPayload } from '../../types/common';

@injectable()
class ChannelEventHandler {
  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.Peers) private readonly peers: Peers,
    @inject(TOKEN.ClientEventEmitter) private readonly clientEventEmitter: EnhancedEventEmitter,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
    @inject(TOKEN.ChannelStateSyncEventHandler)
    private readonly channelStateSyncEventHandler: ChannelStateSyncEventHandler,
    @inject(TOKEN.ChannelStateSynchronizer) private readonly channelStateSynchronizer: ChannelStateSynchronizer,
  ) {
    this.#logger = loggerFactory('ChannelEvents');
  }

  public async handleChannelEvent(event: ChannelEvent): Promise<void> {
    switch (event.eventType) {
      case ChannelEventType.CHANNEL_EVENT_TYPE_CHANNEL_STATE:
        await this.handleChannelState(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_CHANNEL_STATE_SHORT:
        this.handleChannelStateShort(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_CHANNEL_MANAGEMENT_EVENT:
        this.handleChannelManagementEvent(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_PEER_JOINED:
        this.handlePeerJoined(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_PEER_LEFT:
        this.handlePeerLeft(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_PEER_APP_DATA_CHANGED:
        this.handlePeerAppDataChanged(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_PEER_GROUP_CHANGED:
        this.handlePeerGroupsChanged(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_CHANNEL_ACTIVITY_CONFIRMATION_ACQUIRED:
        this.handleChannelActivityConfirmationAcquired();
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_CHANNEL_ACTIVITY_CONFIRMATION_EXPIRED:
        this.handleChannelActivityConfirmationExpired();
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_CHANNEL_ACTIVITY_CONFIRMATION_REQUIRED:
        this.handleChannelActivityConfirmationRequired(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_CHANNEL_PRODUCE_PERMISSIONS_CHANGED:
        this.handleChannelProducePermissionsChanged(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_PEER_PRODUCE_PERMISSIONS_CHANGED:
        this.handlePeerProducePermissionsChanged(event);
        break;

      case ChannelEventType.CHANNEL_EVENT_TYPE_CHANNEL_PRODUCE_PERMISSIONS_DELETED:
        this.handleChannelProducePermissionsDeleted(event);
        break;

      default:
        throw new Error(`Unsupported event type: ${ event.eventType }`);
    }
  }

  private handleChannelManagementEvent(event: ChannelEvent): void {
    if (!event.channelManagementEvent) {
      throw new Error('Missing channelManagementEvent in CHANNEL_MANAGEMENT_EVENT');
    }

    const channelManagementEvent = event.channelManagementEvent.event;
    this.clientEventEmitter.safeEmit(CLIENT_EVENTS.channelEvent, channelManagementEvent);
    this.#logger.debug('New channel event', { event: channelManagementEvent });
  }

  private handlePeerJoined(event: ChannelEvent): void {
    if (!event.peer) {
      throw new Error('Missing peer in PEER_APP_DATA_CHANGED event');
    }

    const peer = this.peers.add(convertChannelMessagePeerToPeerResponse(event.peer));
    this.#logger.debug('Peer joined to the channel', { peer: logResponse(peer) });
  }

  private handlePeerLeft(event: ChannelEvent): void {
    if (!event.peer) {
      throw new Error('Missing peer in PEER_LEFT event');
    }

    this.peers.remove(event.peer.id).catch((error: unknown) => {
      this.#logger.debug('Failed to remove peer', {
        error: serializeError(error),
      });
    });

    this.#logger.debug('Peer left the channel', { peerId: event.peer.id });
  }

  private async handleChannelState(event: ChannelEvent): Promise<void> {
    if (!event.channelState) {
      throw new Error('Missing channelState in CHANNEL_STATE event');
    }

    try {
      await this.channelStateSynchronizer.handleChannelStateEvent(event.channelState);
    } catch (error) {
      this.#logger.error('Failed to restore channel state', {
        case: 'restoreLocalState',
        error: serializeError(error),
      });
    }
  }

  private handleChannelStateShort(event: ChannelEvent): void {
    if (!event.channelStateShort) {
      throw new Error('Missing channelStateShort in CHANNEL_STATE_SHORT event');
    }

    this.channelStateSyncEventHandler.handleChannelStateShort(event.channelStateShort).catch((error: unknown) => {
      this.#logger.error('Failed to handle channel state short event', { error: serializeError(error) });
    });
  }

  private handlePeerAppDataChanged(event: ChannelEvent): void {
    if (!event.peer) {
      throw new Error('Missing peer in PEER_APP_DATA_CHANGED event');
    }

    const peer = this.peers.get(event.peer.id);

    if (peer) {
      peer.observer.emit(CHANNEL_EVENTS.updatePeerAppData, event.peer.appData);
    }
  }

  private handlePeerGroupsChanged(event: ChannelEvent): void {
    if (!event.peer) {
      throw new Error('Missing peer in PEER_GROUP_CHANGED event');
    }

    const peer = this.peers.get(event.peer.id);
    peer?.handleGroupsChange(event.peer.groups.map(convertChannelMessagePeerGroupsToPeerGroups));
  }

  private handleChannelActivityConfirmationExpired(): void {
    this.clientEventEmitter.safeEmit(CLIENT_EVENTS.activityConfirmationExpired);
    this.#logger.info('Notify the channel that it has disconnected due to the activity confirmation expiring', {});
  }

  private handleChannelActivityConfirmationAcquired(): void {
    this.clientEventEmitter.safeEmit(CLIENT_EVENTS.activityConfirmationAcquired);
    this.#logger.info('Notify the channel that the activity has been acquired.', {});
  }

  private handleChannelActivityConfirmationRequired(event: ChannelEvent): void {
    if (!event.channelActivityConfirmationTime) {
      throw new Error('Missing channelActivityConfirmationTime in CHANNEL_ACTIVITY_CONFIRMATION_REQUIRED event');
    }

    const eventPayload = event.channelActivityConfirmationTime as ActivityConfirmationRequiredPayload;
    this.clientEventEmitter.safeEmit(CLIENT_EVENTS.activityConfirmationRequired, eventPayload);
    this.#logger.info('Notify the channel that activity confirmation is required', {
      event: event.channelActivityConfirmationTime,
    });
  }

  private handleChannelProducePermissionsChanged(event: ChannelEvent): void {
    if (!event.channelProducePermissions) {
      throw new Error('Missing channelProducePermissions in CHANNEL_PRODUCE_PERMISSIONS_CHANGED event');
    }

    const { groups } = event.channelProducePermissions;
    const peers = this.peers.all().filter((peer) => peer
      .groups
      .some((group) => groups.includes(convertPeerGroupsToChannelMessagePeerGroups(group))));

    const producePermissions = event
      .channelProducePermissions
      .producePermissions.map(convertChannelMessageProducerLabelsToTrackLabels);

    const changedProducePermissions = event
      .channelProducePermissions
      .changedProducePermissions.map(convertChannelMessageProducerLabelsToTrackLabels);

    peers.forEach((peer) => {
      const newProducePermissions = Array.from(new Set([...peer.producePermissions, ...changedProducePermissions]))
        .filter((label) => {
          return (changedProducePermissions.includes(label) ? producePermissions.includes(label) : true);
        });

      peer.handleProducePermissionsChange(newProducePermissions);
    });

    this.#logger.info('Peers changed produce permissions', { groups, producePermissions });
  }

  private handlePeerProducePermissionsChanged(event: ChannelEvent): void {
    if (!event.peer) {
      throw new Error('Missing peer in PEER_PRODUCE_PERMISSIONS_CHANGED event');
    }

    const peer = this.peers.get(event.peer.id);

    if (!peer) {
      return;
    }

    const producePermissions = event.peer.producePermissions.map(convertChannelMessageProducerLabelsToTrackLabels);
    peer.handleProducePermissionsChange(producePermissions);
  }

  private handleChannelProducePermissionsDeleted(event: ChannelEvent): void {
    if (!event.channelProducePermissions) {
      throw new Error('Missing channelProducePermissions in CHANNEL_PRODUCE_PERMISSIONS_DELETED event');
    }

    const { groups } = event.channelProducePermissions;
    const peers = this.peers.all().filter((peer) => peer
      .groups
      .some((group) => groups.includes(convertPeerGroupsToChannelMessagePeerGroups(group))));

    const producePermissions = event
      .channelProducePermissions
      .producePermissions
      .map(convertChannelMessageProducerLabelsToTrackLabels);

    peers.forEach((peer) => {
      const newProducePermissions = peer
        .producePermissions
        .filter((permission) => !producePermissions.includes(permission));

      peer.handleProducePermissionsChange(newProducePermissions);
    });

    this.#logger.info('Peers deleted produce permissions', { groups, producePermissions });
  }
}

export default ChannelEventHandler;
